import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, Smartphone, Layout, Palette,
  Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import ServicesSection from '../components/home/ServicesSection';
import SEOHead from '@/components/SEOHead';
import { createPageUrl } from '@/utils';

const DELIVERABLES = [
  {
    icon: Globe,
    title: 'Web Development',
    tagline: 'Enterprise-grade custom sites',
    timeline: '2 – 4 weeks',
    price: 'From £1,500',
    items: [
      'Fully responsive across all devices',
      'SEO-optimised architecture',
      'CMS integration (Contentful, Sanity, Notion)',
      'Performance budget < 2 s LCP',
      '3 rounds of revision included',
    ],
  },
  {
    icon: Layout,
    title: 'Landing Pages',
    tagline: 'Conversion-focused single pages',
    timeline: '3 – 7 days',
    price: 'From £400',
    items: [
      'Above-the-fold copy & layout',
      'A/B test ready with split-URL support',
      'Form integration (HubSpot, Mailchimp)',
      'Analytics & heat-map setup',
      '2 rounds of revision included',
    ],
  },
  {
    icon: Smartphone,
    title: 'App Development',
    tagline: 'iOS & Android native or cross-platform',
    timeline: '6 – 12 weeks',
    price: 'From £4,000',
    items: [
      'React Native or Swift / Kotlin',
      'App Store & Google Play submission',
      'Push notifications',
      'Offline-first data sync',
      '4 rounds of revision included',
    ],
  },
  {
    icon: Palette,
    title: 'UI/UX Design',
    tagline: 'Research-backed interface design',
    timeline: '1 – 3 weeks',
    price: 'From £800',
    items: [
      'User research & persona mapping',
      'Wireframes → high-fidelity Figma',
      'Interactive prototype for stakeholder sign-off',
      'Design system & component library',
      'Developer hand-off with design tokens',
    ],
  },
];

const PROCESS = [
  { step: '01', title: 'Discovery call', desc: 'A 30-minute session to understand your goals, audience, and success criteria.' },
  { step: '02', title: 'Proposal & SOW', desc: 'We send a scoped Statement of Work with timeline, milestones, and fixed price.' },
  { step: '03', title: 'Design & build', desc: 'Our team designs, codes, and tests your project in focused two-week sprints.' },
  { step: '04', title: 'Review & launch', desc: 'You review each milestone, request revisions, and sign off before we go live.' },
];

const SERVICES_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'BuildersLab Services',
  url: 'https://builderslab.net/Services',
  itemListElement: [
    { '@type': 'ListItem', position: 1, item: { '@type': 'Service', name: 'Web Development', description: 'Enterprise-grade custom sites from £1,500', provider: { '@type': 'Organization', name: 'BuildersLab' } } },
    { '@type': 'ListItem', position: 2, item: { '@type': 'Service', name: 'Landing Pages', description: 'Conversion-focused single pages from £400', provider: { '@type': 'Organization', name: 'BuildersLab' } } },
    { '@type': 'ListItem', position: 3, item: { '@type': 'Service', name: 'App Development', description: 'iOS & Android native or cross-platform apps from £4,000', provider: { '@type': 'Organization', name: 'BuildersLab' } } },
    { '@type': 'ListItem', position: 4, item: { '@type': 'Service', name: 'UI/UX Design', description: 'Research-backed interface design from £800', provider: { '@type': 'Organization', name: 'BuildersLab' } } },
  ],
};

export default function Services() {
  return (
    <div className="bg-zinc-950 min-h-screen">
      <SEOHead
        title="Services — Web Development, App Design & AI Builder | BuildersLab"
        description="Custom web development, landing pages, iOS/Android apps, and UI/UX design by BuildersLab. Fixed-price projects delivered in days, not months."
        ogUrl="https://builderslab.net/Services"
        noIndex={false}
        jsonLd={SERVICES_JSONLD}
      />

      {/* Existing ServicesSection at the top */}
      <div className="pt-16">
        <ServicesSection />
      </div>

      {/* Deliverables grid */}
      <section className="py-24 bg-zinc-950 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div
           
           
           
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 mb-6">
              <span className="text-xs font-bold text-zinc-300 uppercase tracking-widest">Deliverables</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">
              Exactly what you get
            </h2>
            <p className="text-zinc-400 mt-4 text-lg max-w-xl mx-auto">
              No ambiguity. Every engagement starts with a fixed scope, fixed price, and fixed timeline.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {DELIVERABLES.map((service, i) => (
              <div
                key={service.title}
               
               
               
               
                className="bg-zinc-900/50 border border-white/8 rounded-3xl p-8 hover:border-white/15 transition-colors"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
                    <service.icon className="w-6 h-6 text-orange-400" />
                  </div>
                  <div className="text-right">
                    <p className="text-orange-400 font-bold text-sm">{service.price}</p>
                    <p className="text-zinc-500 text-xs flex items-center gap-1 justify-end mt-1">
                      <Clock className="w-3 h-3" /> {service.timeline}
                    </p>
                  </div>
                </div>
                <h3 className="text-white font-bold text-xl mb-1">{service.title}</h3>
                <p className="text-zinc-500 text-sm mb-5">{service.tagline}</p>
                <ul className="space-y-2.5">
                  {service.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-zinc-400 text-sm">
                      <span className="text-orange-400 shrink-0">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-zinc-950 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <div
           
           
           
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 mb-6">
              <span className="text-xs font-bold text-zinc-300 uppercase tracking-widest">How It Works</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">
              Simple from start to launch
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS.map((step, i) => (
              <div
                key={step.step}
               
               
               
               
                className="relative"
              >
                <div className="text-5xl font-black text-white/5 mb-4 leading-none">{step.step}</div>
                <h4 className="text-white font-bold text-base mb-2">{step.title}</h4>
                <p className="text-zinc-500 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-zinc-950 border-t border-white/5">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div
           
           
           
          >
            <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight mb-5">
              Ready to build?
            </h2>
            <p className="text-zinc-400 text-lg mb-10">
              Tell us what you need. We'll reply within one business day with a scope and quote.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={createPageUrl('Contact')}>
                <Button
                  size="lg"
                  className="bg-white text-zinc-950 hover:bg-zinc-200 font-bold rounded-xl h-14 px-8 text-sm group"
                >
                  Book a discovery call
                </Button>
              </Link>
              <Link to={createPageUrl('AIBuilder')}>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white rounded-xl h-14 px-8 text-sm group"
                >
                  Try the AI Builder
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
